import { Select, SelectOption, Tooltip, useAlerts } from '@catapultsports/referee-react';
import { SavingState, useActivityEditor } from 'activity-editor/context';
import { EVENTS } from 'analytics/analytics-events';
import { useCreateEntityTags, useDeleteEntityTag } from 'api/entity-tags';
import { useGetAllTagsOfTagType } from 'api/tags';
import { useTranslations } from 'i18n';
import mixpanel from 'mixpanel-browser';
import { Tags } from 'oas';
import * as React from 'react';
import { useQueryClient } from 'react-query';
import { DAY_CODE_TAG_TYPE, getValidationErrors } from 'shared';

const generateOptions = (tags: Tags[] | undefined, selectedDayCodeTagId: string): SelectOption[] => {
    return (
        [...(tags || [])]
            .sort((tag1, tag2) => tag1.name?.localeCompare(tag2.name ?? '') ?? 0)
            ?.map((tag) => {
                return {
                    id: tag.id ?? '',
                    value: tag.name ?? '',
                    isSelected: selectedDayCodeTagId === tag.id,
                };
            }) ?? []
    );
};

interface DayCodeSelectProps {
    currentActivityId: string | undefined;
    selectedDayCodeTagId: string;
    onIsSaving?: (isSaving: boolean) => void;
}

export const ActivityDayCodeSelect: React.FunctionComponent<DayCodeSelectProps> = ({
    currentActivityId,
    selectedDayCodeTagId,
    onIsSaving,
}) => {
    const { __ } = useTranslations();
    const { showAlert } = useAlerts();
    const { setSavingState } = useActivityEditor();
    const { data: tags } = useGetAllTagsOfTagType(DAY_CODE_TAG_TYPE);
    const { mutateAsync: createEntityTag, isLoading } = useCreateEntityTags();
    const { mutateAsync: deleteEntityTag } = useDeleteEntityTag();
    const queryClient = useQueryClient();

    const availableDayCodeTags: SelectOption[] = React.useMemo(
        () => generateOptions(tags, selectedDayCodeTagId),
        [selectedDayCodeTagId, tags],
    );

    const defaultOption = availableDayCodeTags.find((option) => option.id === selectedDayCodeTagId);

    React.useEffect(() => onIsSaving && onIsSaving(isLoading), [onIsSaving, isLoading]);

    const handleOptionSelect = React.useCallback(
        async (selectedOptions: SelectOption[]) => {
            const selectedOptionId: string = selectedOptions?.map(
                (selectedOption) => selectedOption.id?.toString() ?? '',
            )?.[0];

            // check if selected option is same as current day code id and day code tag id is present
            if (selectedOptionId !== selectedDayCodeTagId && currentActivityId) {
                try {
                    setSavingState(SavingState.saving);
                    if (selectedDayCodeTagId) {
                        // remove tag
                        await deleteEntityTag({
                            table_id: currentActivityId,
                            tag_id: selectedDayCodeTagId,
                            table_name: 'activity',
                        });
                    }
                    // add entity tag
                    await createEntityTag(
                        [
                            {
                                table_id: currentActivityId,
                                tag_id: selectedOptionId,
                                table_name: 'activity',
                            },
                        ],
                        {
                            onSuccess: () => {
                                queryClient.invalidateQueries(['activities']);
                            },
                        },
                    );

                    setSavingState(SavingState.saved);
                    mixpanel.track(EVENTS.editing.dayCodeTagChanged, {
                        tag_name: availableDayCodeTags.find((option) => option.id === selectedOptionId)?.value,
                    });
                } catch (responseError) {
                    showAlert(...getValidationErrors(responseError), { variant: 'alert' });
                }
            }
        },
        [
            availableDayCodeTags,
            createEntityTag,
            currentActivityId,
            deleteEntityTag,
            setSavingState,
            showAlert,
            queryClient,
            selectedDayCodeTagId,
        ],
    );

    return (
        <div key={currentActivityId}>
            <Tooltip position="right" text={__(`misc.day_code`)}>
                <Select
                    testId={'day-code-tag'}
                    menuItems={availableDayCodeTags}
                    defaultOption={defaultOption}
                    onSelectItem={handleOptionSelect}
                    size="small"
                    maxDisplayValues={2}
                />
            </Tooltip>
        </div>
    );
};
